import React, { useCallback } from 'react';
import { useId } from '@reach/auto-id';

import { useField, useFieldDescription } from '../../context/form.context';
import { ErrorMessage } from '../error-message.component';
import { Option, SearchSelect, SearchSelectProps } from '../search-select';
import { FieldLabel } from './field-label.component';

export type SearchSelectFieldProps = {
  name: string;
  label?: string;
  className?: string;
  showRequiredIndicator?: boolean;
} & Omit<SearchSelectProps, 'value' | 'hasError' | 'inputId' | 'onChange'>;

export const SearchSelectField = ({
  name,
  label: labelProp,
  className,
  showRequiredIndicator = true,
  ...rest
}: SearchSelectFieldProps) => {
  const [{ value }, meta, helpers] = useField(name);
  const { fieldLabel, required } = useFieldDescription(name);
  const label = labelProp ?? fieldLabel;
  const { error, touched } = meta;
  const hasError = Boolean(error && touched);
  const inputId = useId()!;
  const errorId = useId()!;

  const handleChange = useCallback(
    (value: ReadonlyArray<Option>) => {
      helpers.setValue(value);
    },
    [helpers]
  );

  return (
    <div className={className}>
      <FieldLabel
        htmlFor={inputId}
        hasError={hasError}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        {label}
      </FieldLabel>

      <SearchSelect
        inputId={inputId}
        value={value}
        hasError={hasError}
        onChange={handleChange}
        {...rest}
      />
      {hasError && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
};
